import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, CheckCircle, Brain, Target, Trophy, BarChart3, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const hdr = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });

// NextGEN SJT Questions (18-24 age group)
const NEXTGEN_QUESTIONS = [
  { id: 'sjt1', domain: 'Work Readiness', scenario: 'You arrive at your internship and your supervisor is out sick. No one tells you what to do.',
    options: [{text: 'Wait at your desk until someone notices you', score: 1}, {text: 'Find another staff member and ask how you can help today', score: 3}, {text: 'Use the time to scroll your phone — not your fault', score: 0}, {text: 'Leave early since your supervisor isn\'t there', score: 0}], correct: 1 },
  { id: 'sjt2', domain: 'Communication', scenario: 'During a team project, a coworker keeps interrupting you in meetings. It\'s starting to affect your contributions.',
    options: [{text: 'Interrupt them back to make a point', score: 1}, {text: 'Stop contributing to meetings entirely', score: 0}, {text: 'Ask to speak with them privately and explain how their interruptions affect you', score: 3}, {text: 'Complain about them to your supervisor', score: 1}], correct: 2 },
  { id: 'sjt3', domain: 'Financial Literacy', scenario: 'You receive your first paycheck of $800. Your friend invites you to a concert that costs $150. Your phone bill is due ($80) and you wanted to start saving.',
    options: [{text: 'Go to the concert — you earned it and you deserve fun', score: 1}, {text: 'Skip the concert, pay your bill, and put $200 into savings before spending on anything else', score: 2}, {text: 'Pay your bill first, put $150 in savings, then decide about the concert with what\'s left', score: 3}, {text: 'Put the entire check in savings and borrow money for your phone bill', score: 1}], correct: 2 },
  { id: 'sjt4', domain: 'Professionalism', scenario: 'You accidentally CC\'d the wrong person on a sensitive email at work. You realize it 10 minutes later.',
    options: [{text: 'Hope nobody notices and delete it from your sent folder', score: 0}, {text: 'Immediately tell your supervisor what happened and ask how to handle it', score: 3}, {text: 'Send a follow-up email to the wrong person asking them to ignore it', score: 2}, {text: 'Blame the email system for auto-filling the wrong address', score: 0}], correct: 1 },
  { id: 'sjt5', domain: 'Problem Solving', scenario: 'You\'re assigned a project with a tight deadline, but you realize halfway through that the approach isn\'t working.',
    options: [{text: 'Keep going with the current approach — you\'ve already invested time', score: 1}, {text: 'Abandon the project and tell your supervisor it can\'t be done', score: 0}, {text: 'Pause, identify what\'s not working, brainstorm 2-3 alternatives, then pick the best one and communicate the change to your supervisor', score: 3}, {text: 'Ask a coworker to take over the project', score: 0}], correct: 2 },
  { id: 'sjt6', domain: 'Teamwork', scenario: 'You\'re working on a group presentation. One team member hasn\'t contributed anything and the deadline is tomorrow.',
    options: [{text: 'Do their part yourself — it\'s easier than confronting them', score: 1}, {text: 'Reach out to them directly, ask if everything is okay, and offer to help them catch up on their section', score: 3}, {text: 'Report them to your supervisor immediately', score: 1}, {text: 'Remove their name from the presentation', score: 0}], correct: 1 },
  { id: 'sjt7', domain: 'Communication', scenario: 'Your supervisor gives you feedback that your reports need more detail. You thought they were fine.',
    options: [{text: 'Argue that your reports are detailed enough', score: 0}, {text: 'Say "okay" but keep doing them the same way', score: 0}, {text: 'Ask for a specific example of what "more detail" looks like, then adjust your next report accordingly', score: 3}, {text: 'Feel offended and ask to be reassigned to different tasks', score: 0}], correct: 2 },
  { id: 'sjt8', domain: 'Professionalism', scenario: 'You overhear two coworkers gossiping about another colleague\'s personal life during lunch.',
    options: [{text: 'Join in — everyone gossips', score: 0}, {text: 'Don\'t participate but don\'t say anything either. Redirect the conversation if it comes to you.', score: 3}, {text: 'Record the conversation as evidence', score: 0}, {text: 'Confront them loudly in front of everyone', score: 0}], correct: 1 },
  { id: 'sjt9', domain: 'Financial Literacy', scenario: 'A website offers you a "Buy Now, Pay Later" option for a $200 pair of sneakers. You have $250 in savings but no income for 2 weeks.',
    options: [{text: 'Use Buy Now Pay Later — the payments are small', score: 0}, {text: 'Buy them with your savings — you need them now', score: 0}, {text: 'Wait until your next paycheck, check if you still want them after 48 hours, and only buy if they fit your budget', score: 3}, {text: 'Ask a friend to lend you the money', score: 0}], correct: 2 },
  { id: 'sjt10', domain: 'Problem Solving', scenario: 'You\'re tasked with organizing a community event but the venue cancels 3 days before. Your supervisor is unavailable.',
    options: [{text: 'Cancel the event — there\'s no other option', score: 0}, {text: 'Research 3 alternative venues, estimate costs and logistics for each, then present your recommendation when your supervisor is available', score: 3}, {text: 'Panic and wait for your supervisor to come back', score: 0}, {text: 'Post on social media asking if anyone has a space', score: 1}], correct: 1 },
];

const DOMAIN_COLORS = {
  'Work Readiness': '#E86C3D',
  'Communication': '#3B82F6',
  'Financial Literacy': '#10B981',
  'Professionalism': '#8B5CF6',
  'Problem Solving': '#F59E0B',
  'Teamwork': '#EC4899',
};

const SJTAssessmentPage = ({ user }) => {
  const navigate = useNavigate();
  const [view, setView] = useState('intro'); // intro, assessment, results
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    const checkExisting = async () => {
      try {
        const res = await fetch(`${API}/api/sjt-assessment/my-results`, { headers: hdr() });
        if (res.ok) {
          const d = await res.json();
          if (d && d.completed) { setExisting(d); setResults(d); setView('results'); }
        }
      } catch (e) { console.error('Assessment check failed:', e); }
    };
    checkExisting();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectAnswer = (qi, ai) => {
    setAnswers({ ...answers, [qi]: ai });
  };

  const submitAssessment = async () => {
    setSubmitting(true);
    try {
      const scored = NEXTGEN_QUESTIONS.map((q, i) => ({
        question_id: q.id,
        domain: q.domain,
        selected: answers[i],
        score: answers[i] !== undefined ? q.options[answers[i]].score : 0,
        max_score: 3
      }));
      
      const res = await fetch(`${API}/api/sjt-assessment/submit`, {
        method: 'POST', headers: hdr(),
        body: JSON.stringify({ responses: scored })
      });
      if (res.ok) {
        const d = await res.json();
        setResults(d);
        setView('results');
        toast.success(`Assessment complete! +${d.points_earned} points`);
      }
    } catch (e) { toast.error('Failed to submit'); }
    setSubmitting(false);
  };

  const questions = NEXTGEN_QUESTIONS;

  // ==================== INTRO ====================
  if (view === 'intro') {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F5F3EF' }}>
        <div style={{ background: 'linear-gradient(135deg, #0A0E1A 0%, #1B2D4F 100%)' }} className="py-12 px-4">
          <div className="max-w-lg mx-auto text-center">
            <button onClick={() => navigate(-1)} className="p-2 rounded-xl bg-white/10 hover:bg-white/15 mb-4 float-left">
              <ArrowLeft className="w-5 h-5 text-white/70" />
            </button>
            <div className="clear-both" />
            <Brain className="w-16 h-16 text-orange-400 mx-auto mb-4" />
            <h1 className="text-2xl font-black text-white mb-2">Workplace Skills Assessment</h1>
            <p className="text-white/60 text-sm">10 real workplace scenarios. Pick what you'd actually do. No trick questions.</p>
          </div>
        </div>
        <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
          <Card className="p-4 rounded-2xl">
            <h3 className="font-bold text-sm mb-2">What This Measures</h3>
            <div className="space-y-2">
              {Object.entries(DOMAIN_COLORS).map(([domain, color]) => (
                <div key={domain} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                  <span className="text-sm text-gray-700">{domain}</span>
                </div>
              ))}
            </div>
          </Card>
          <Card className="p-4 rounded-2xl bg-amber-50 border-amber-100">
            <p className="text-sm text-amber-800">There are no "wrong" answers — just pick what you'd genuinely do. This helps us understand where you are so we can support your growth.</p>
          </Card>
          <Card className="p-4 rounded-2xl">
            <div className="flex justify-between text-sm text-gray-600">
              <span>10 questions</span>
              <span>~8 minutes</span>
              <span>+75 XP</span>
            </div>
          </Card>
          <Button onClick={() => setView('assessment')} className="w-full py-5 rounded-2xl bg-orange-500 hover:bg-orange-600 text-white font-bold text-base">
            Start Assessment <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  // ==================== ASSESSMENT ====================
  if (view === 'assessment') {
    const q = questions[currentQ];
    const domainColor = DOMAIN_COLORS[q.domain] || '#E86C3D';
    return (
      <div className="min-h-screen pb-24" style={{ backgroundColor: '#F5F3EF' }}>
        <div className="sticky top-0 z-40 bg-white border-b border-gray-100 px-4 py-3">
          <div className="max-w-lg mx-auto">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold" style={{ color: domainColor }}>{q.domain}</span>
              <span className="text-xs text-gray-400">{currentQ + 1} of {questions.length}</span>
            </div>
            <Progress value={((currentQ + 1) / questions.length) * 100} className="h-2" />
          </div>
        </div>

        <div className="max-w-lg mx-auto px-4 py-6">
          <AnimatePresence mode="wait">
            <motion.div key={currentQ} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}>
              <Card className="p-5 rounded-2xl mb-4 border-l-4" style={{ borderLeftColor: domainColor }}>
                <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: domainColor }}>Scenario</p>
                <p className="text-base text-gray-900 leading-relaxed font-medium">{q.scenario}</p>
              </Card>

              <p className="text-sm font-bold text-gray-700 mb-3">What would you do?</p>
              <div className="space-y-2">
                {q.options.map((opt, oi) => (
                  <button key={oi} onClick={() => selectAnswer(currentQ, oi)}
                    className={`w-full p-4 rounded-2xl text-left text-sm transition-all active:scale-[0.98] ${answers[currentQ] === oi
                      ? 'bg-white ring-2 shadow-md font-medium' : 'bg-white border border-gray-100 text-gray-600 hover:bg-gray-50'}`}
                    style={answers[currentQ] === oi ? { ringColor: domainColor, color: domainColor } : {}}>
                    <div className="flex items-start gap-3">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${answers[currentQ] === oi ? 'text-white' : 'bg-gray-100 text-gray-500'}`}
                        style={answers[currentQ] === oi ? { backgroundColor: domainColor } : {}}>
                        {String.fromCharCode(65 + oi)}
                      </div>
                      <span className="flex-1 mt-0.5">{opt.text}</span>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-gray-100 px-4 py-3">
          <div className="max-w-lg mx-auto flex gap-3">
            <Button variant="outline" onClick={() => setCurrentQ(Math.max(0, currentQ - 1))} disabled={currentQ === 0} className="rounded-2xl">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            {currentQ < questions.length - 1 ? (
              <Button onClick={() => { if (answers[currentQ] !== undefined) setCurrentQ(currentQ + 1); else toast.error('Pick an answer first'); }}
                className="flex-1 rounded-2xl bg-orange-500 hover:bg-orange-600 text-white font-bold" disabled={answers[currentQ] === undefined}>
                Next <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={submitAssessment} disabled={submitting || Object.keys(answers).length < questions.length}
                className="flex-1 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Submit Assessment
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ==================== RESULTS ====================
  if (view === 'results' && results) {
    const domainScores = results.domain_scores || {};
    return (
      <div className="min-h-screen pb-8" style={{ backgroundColor: '#F5F3EF' }}>
        <div style={{ background: 'linear-gradient(135deg, #0A0E1A 0%, #1B2D4F 100%)' }} className="py-8 px-4">
          <div className="max-w-lg mx-auto text-center">
            <Trophy className="w-16 h-16 text-amber-400 mx-auto mb-3" />
            <h1 className="text-2xl font-black text-white">Assessment Complete!</h1>
            <p className="text-4xl font-black text-orange-400 mt-2">
              {results.total_score != null && results.max_score != null 
                ? `${results.total_score}/${results.max_score}` 
                : 'Completed'}
            </p>
            <p className="text-white/50 text-sm">{results.level || 'Assessment submitted'}</p>
          </div>
        </div>
        <div className="max-w-lg mx-auto px-4 py-5 space-y-4">
          {/* Domain Breakdown */}
          <Card className="p-4 rounded-2xl">
            <h3 className="font-bold text-sm mb-3">Skills Breakdown</h3>
            <div className="space-y-3">
              {Object.entries(domainScores).map(([domain, data]) => (
                <div key={domain}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold" style={{ color: DOMAIN_COLORS[domain] || '#666' }}>{domain}</span>
                    <span className="text-xs font-bold text-gray-500">{data.score}/{data.max}</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full">
                    <div className="h-2 rounded-full transition-all" style={{ width: `${(data.score / data.max) * 100}%`, backgroundColor: DOMAIN_COLORS[domain] || '#666' }} />
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* AI Feedback */}
          {results.ai_feedback && (
            <>
              {results.ai_feedback.strengths?.length > 0 && (
                <Card className="p-4 rounded-2xl bg-emerald-50 border-emerald-100">
                  <p className="text-xs font-bold text-emerald-700 uppercase mb-2">Your Strengths</p>
                  {results.ai_feedback.strengths.map((s, i) => <p key={i} className="text-sm text-emerald-800 mb-1">+ {s}</p>)}
                </Card>
              )}
              {results.ai_feedback.growth_areas?.length > 0 && (
                <Card className="p-4 rounded-2xl bg-amber-50 border-amber-100">
                  <p className="text-xs font-bold text-amber-700 uppercase mb-2">Areas to Develop</p>
                  {results.ai_feedback.growth_areas.map((g, i) => <p key={i} className="text-sm text-amber-800 mb-1">~ {g}</p>)}
                </Card>
              )}
              {results.ai_feedback.recommendations?.length > 0 && (
                <Card className="p-4 rounded-2xl bg-blue-50 border-blue-100">
                  <p className="text-xs font-bold text-blue-700 uppercase mb-2">Recommendations</p>
                  {results.ai_feedback.recommendations.map((r, i) => <p key={i} className="text-sm text-blue-800 mb-1">→ {r}</p>)}
                </Card>
              )}
            </>
          )}

          <Button onClick={() => navigate(-1)} className="w-full py-5 rounded-2xl bg-orange-500 hover:bg-orange-600 text-white font-bold">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return null;
};

export default SJTAssessmentPage;
